import { Add, Delete, Edit, Home, Refresh } from "@mui/icons-material";
import {
  Box,
  Breadcrumbs,
  CardActions,
  CardContent,
  CircularProgress,
  IconButton,
  Tooltip,
  Typography
} from "@mui/material";
import { COLORS } from "assets";
import { DataTable, KebabMenu, PageToolbar, StatusPill, StyledCard } from "components";
import StyledTablePagination from "components/StyledTablePagination";
import { ENDPOINTS } from "config/constants";
import { useContextController } from "context";
import { useFetch, useTitle } from "hooks";
import useTranslation from "hooks/useTranslation";
import PrivateLayout from "layouts/PrivateLayout";
import { ChangeEventHandler, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { UserModel } from "types";
import { dateFormat } from "utils";
import EditUser from "./[id]";
import AddUser from "./add";
import RemoveUser from "./remove";

interface Data {
  users?: UserModel[];
  totalRecords?: number;
}

export default function Users() {
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<UserModel>();
  const [removing, setRemoving] = useState<UserModel>();
  const [{ userData }] = useContextController();
  const [reload, setReload] = useState(false);
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(10);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState<keyof UserModel | undefined>("createdAt");
  const [isAscending, setAscending] = useState(false);

  const t = useTranslation();
  const { isLoading, response, error, callFetch: callFetchGet } = useFetch<Data>("GET");
  const { users, totalRecords } = useMemo(() => {
    const data = response && response.data;
    if (data) {
      return data;
    }
    return {};
  }, [response]);
  const searchWaitRef = useRef<NodeJS.Timeout>();
  const onSort = useCallback(
    (val: keyof UserModel) => {
      if (val === sort) {
        if (isAscending) {
          setAscending(false);
        } else {
          setSort(undefined);
        }
      } else {
        setSort(val);
        setAscending(true);
      }
    },
    [sort, isAscending]
  );
  const onReload = useCallback(() => setReload((prevState) => !prevState), []);
  const onPageChange = (_: any, page: number) => setPage(page);
  const onRowsPerPageChange: ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement> = ({ target: { value } }) => {
    setLimit(Number.parseInt(value, 10));
    setPage(0);
  };

  useEffect(() => {
    let params = `?limit=${limit}&page=${page + 1}`;
    if (search) {
      params += `&search=${search}`;
    }
    if (sort) {
      params += `&sort=${isAscending ? "" : "-"}${sort}`;
    }
    if (searchWaitRef.current) {
      clearTimeout(searchWaitRef.current);
    }
    searchWaitRef.current = setTimeout(() => {
      callFetchGet(ENDPOINTS.users + params).catch(console.error);
    }, 400);
    return () => {
      if (searchWaitRef.current) clearTimeout(searchWaitRef.current);
    };
  }, [page, limit, search, sort, isAscending, reload, callFetchGet]);

  useEffect(() => setPage(0), [search]);

  useTitle("Users");

  const columns = useMemo(
    () => [
      { header: t["ID"], accessor: "userId" as const },
      { header: t["Name"], accessor: "name" as const, sort: "name" as const },
      { header: t["Email"], accessor: "email" as const, sort: "email" as const },
      {
        header: t["User Type"],
        sort: "type" as const,
        accessor: (row: UserModel) => <StatusPill label={row.type} status={row.type} />
      },
      {
        header: t["Site"],
        accessor: (row: UserModel) => t[row.site || "none"] || row.site || "none"
      },
      {
        header: t["Added On"],
        sort: "createdAt" as const,
        accessor: (row: UserModel) => dateFormat(row.createdAt)
      },
      {
        header: t["Actions"],
        align: "center" as const,
        accessor: (row: UserModel) => (
          <KebabMenu
            items={[
              {
                key: "edit",
                label: t["Edit"],
                icon: <Edit fontSize="small" />,
                onClick: () => setEditing(row)
              },
              {
                key: "delete",
                label: t["Delete"],
                icon: <Delete fontSize="small" />,
                destructive: true,
                onClick: () => setRemoving(row)
              }
            ]}
          />
        )
      }
    ],
    [t]
  );

  return (
    <PrivateLayout canAccess={(type) => type === "super"}>
      {adding && <AddUser onSuccess={onReload} onClose={() => setAdding(false)} />}
      {editing && <EditUser user={editing} onSuccess={onReload} onClose={() => setEditing(undefined)} />}
      {removing && <RemoveUser removing={removing} onSuccess={onReload} onClose={() => setRemoving(undefined)} />}
      <PageToolbar
        title={t["User Records"]}
        search={search}
        searchPlaceholder={t["Search users by name or email."]}
        onSearchChange={setSearch}
        breadcrumbs={
          <Breadcrumbs aria-label="breadcrumb">
            <Tooltip title={t["Home"]}>
              <IconButton LinkComponent={Link} {...{ to: "/" }} size="small" sx={{ color: COLORS.surface.secondaryText }}>
                <Home fontSize="small" />
              </IconButton>
            </Tooltip>
            <Typography color="text.primary">{t["Users"]}</Typography>
          </Breadcrumbs>
        }
        actions={
          <>
            {userData?.type !== "user" ? (
              <Tooltip title={t["Add User"]}>
                <IconButton
                  onClick={() => setAdding(true)}
                  sx={{
                    bgcolor: COLORS.surface.card,
                    color: COLORS.surface.primaryText,
                    border: `1px solid ${COLORS.surface.border}`,
                    borderRadius: `${COLORS.radius.sm}px`,
                    "&:hover": { bgcolor: COLORS.surface.tableHeader }
                  }}
                >
                  <Add />
                </IconButton>
              </Tooltip>
            ) : null}
            {isLoading ? (
              <IconButton disabled>
                <CircularProgress size={20} sx={{ color: COLORS.primary }} />
              </IconButton>
            ) : (
              <Tooltip title={t["Refresh"]}>
                <IconButton
                  onClick={onReload}
                  sx={{
                    bgcolor: COLORS.surface.card,
                    color: COLORS.surface.primaryText,
                    border: `1px solid ${COLORS.surface.border}`,
                    borderRadius: `${COLORS.radius.sm}px`,
                    "&:hover": { bgcolor: COLORS.surface.tableHeader }
                  }}
                >
                  <Refresh />
                </IconButton>
              </Tooltip>
            )}
          </>
        }
      />
      <Box width="100%">
        <StyledCard>
          <CardContent sx={{ pt: 2 }}>
            <DataTable
              rows={users || null}
              isLoading={isLoading}
              error={error}
              sort={sort}
              isAscending={isAscending}
              onSort={onSort}
              columns={columns}
            />
          </CardContent>
          <CardActions sx={{ justifyContent: "flex-end" }}>
            <StyledTablePagination
              component="div"
              align="right"
              count={totalRecords || 0}
              page={page}
              onPageChange={onPageChange}
              rowsPerPage={limit}
              onRowsPerPageChange={onRowsPerPageChange}
            />
          </CardActions>
        </StyledCard>
      </Box>
    </PrivateLayout>
  );
}
